import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import { removePointsAndParenthesis } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useCoefficientOne from './useCoefficientOne'

function CoefficientOne({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { variable, formValid, inputRef, answer, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useCoefficientOne({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
      answerState,
      autofocus,
    })

  useEmitInstance(onInstanceChange, variable ? `1${variable} = \\square` : null)

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row items-center mt-2">
          <span className="tracking-widest">
            1<i>{variable}</i>
          </span>
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={3}
              onChange={(value) => onInputChange(removePointsAndParenthesis(value))}
              onEnter={onEnterPress}
              ref={inputRef}
              type="text"
              value={answer}
            />
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(''),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default CoefficientOne
